import type {
  Collection,
  ImportSpecifier,
  JSCodeshift,
  JSXAttribute,
  JSXElement,
  JSXIdentifier,
  JSXMemberExpression,
  JSXNamespacedName,
  JSXSpreadAttribute,
} from 'jscodeshift';

/**
 * Rename a JSX element name if it is a JSXIdentifier.
 */
export const setNameIfJSXIdentifier = (
  elementName: JSXIdentifier | JSXNamespacedName | JSXMemberExpression | undefined,
  newName: string,
): JSXIdentifier | JSXNamespacedName | JSXMemberExpression | undefined => {
  if (elementName?.type === 'JSXIdentifier') {
    return { ...elementName, name: newName };
  }
  return elementName;
};

/**
 * Check if a list of attributes contains a specific attribute by name.
 */
export const hasAttribute = (
  attributes: (JSXAttribute | JSXSpreadAttribute)[] | undefined,
  attributeName: string,
): boolean => {
  return (
    Array.isArray(attributes) &&
    attributes.some(
      (attr): attr is JSXAttribute =>
        attr.type === 'JSXAttribute' &&
        attr.name.type === 'JSXIdentifier' &&
        attr.name.name === attributeName,
    )
  );
};

/**
 * Check if a JSX element's openingElement has a specific attribute.
 */
export const hasAttributeOnElement = (
  element: JSXElement['openingElement'],
  attributeName: string,
): boolean => {
  return hasAttribute(element.attributes, attributeName);
};

/**
 * Add specified attributes to a JSX element's openingElement if they are not already present.
 */
export const addAttributesIfMissing = (
  j: JSCodeshift,
  openingElement: JSXElement['openingElement'],
  attributesToAdd: { attribute: JSXAttribute; name: string }[],
) => {
  if (!Array.isArray(openingElement.attributes)) return;
  const attrs = openingElement.attributes;
  attributesToAdd.forEach(({ attribute, name }) => {
    if (!hasAttributeOnElement(openingElement, name)) {
      attrs.push(attribute);
    }
  });
};

/**
 * Returns a collection of JSX elements that match the specified
 * exported name or names of the found aliases.
 */
export const findJSXElementsByName =
  (root: Collection, j: JSCodeshift) =>
  (exportedName: string, aliases?: Collection<ImportSpecifier>) => {
    const aliasNames = aliases?.size()
      ? aliases.paths().map((path) => path.node.local?.name as string)
      : [];

    return root.find(j.JSXElement).filter((path) => {
      const { name } = path.node.openingElement;
      return (
        name.type === 'JSXIdentifier' &&
        (name.name === exportedName || aliasNames.includes(name.name))
      );
    });
  };

/**
 * Removes an attribute by name from a JSX element's openingElement.
 */
export const removeAttributeByName = (
  j: JSCodeshift,
  openingElement: JSXElement['openingElement'],
  attributeName: string,
) => {
  if (!Array.isArray(openingElement.attributes)) return;
  // eslint-disable-next-line no-param-reassign
  openingElement.attributes = openingElement.attributes.filter((attr) => {
    return !(
      attr.type === 'JSXAttribute' &&
      attr.name.type === 'JSXIdentifier' &&
      attr.name.name === attributeName
    );
  });
};
